import { motion } from "framer-motion";
import { Sparkles, ArrowRight } from "lucide-react";
import { scrollToSection } from "@/lib/scroll";

const GALLERY = [
  {
    url: "https://images.unsplash.com/photo-1682888813913-e13f18692019?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400",
    caption: "Kitchen Deep Clean",
    featured: true,
  },
  {
    url: "https://images.pexels.com/photos/19916730/pexels-photo-19916730.jpeg?auto=compress&cs=tinysrgb&w=800",
    caption: "Bathroom Refresh",
  },
  {
    url: "https://images.unsplash.com/photo-1633505899118-4ca6bd143043?crop=entropy&cs=srgb&fm=jpg&q=80&w=800",
    caption: "Living Room Reset",
  },
  {
    url: "https://images.pexels.com/photos/3935327/pexels-photo-3935327.jpeg?auto=compress&cs=tinysrgb&w=800",
    caption: "Floor Revival",
  },
  {
    url: "https://images.pexels.com/photos/39686955/pexels-photo-39686955.jpeg?auto=compress&cs=tinysrgb&w=800",
    caption: "Hotel-Crisp Bedrooms",
  },
  {
    url: "https://images.unsplash.com/photo-1631193816258-28b44b21e78b?crop=entropy&cs=srgb&fm=jpg&q=80&w=800",
    caption: "Office Reset",
  },
  {
    url: "https://images.unsplash.com/photo-1592506119503-c0b18879bd5a?crop=entropy&cs=srgb&fm=jpg&q=80&w=800",
    caption: "Cabinet & Surface Detail",
  },
  {
    url: "https://images.pexels.com/photos/4680351/pexels-photo-4680351.jpeg?auto=compress&cs=tinysrgb&w=800",
    caption: "Workspace Refresh",
  },
];

const ease = [0.16, 1, 0.3, 1];

const Tile = ({ img, index }) => (
  <motion.figure
    data-testid={`gallery-tile-${index}`}
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.65, delay: (index % 4) * 0.07, ease }}
    className={`group relative overflow-hidden rounded-3xl border border-slate-100 shadow-sm ${
      img.featured ? "col-span-2 row-span-2 min-h-[280px]" : "aspect-square"
    }`}
  >
    <img
      src={img.url}
      alt={img.caption}
      loading="lazy"
      className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-transparent to-transparent opacity-80 transition-opacity duration-500 group-hover:opacity-95" />
    <span className="absolute left-4 top-4 flex items-center gap-1.5 rounded-full bg-teal-500/90 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-950 backdrop-blur-sm">
      <Sparkles className="h-3 w-3" /> After
    </span>
    <figcaption className="absolute bottom-0 left-0 p-5">
      <p className="font-serif text-lg font-semibold tracking-tight text-white">{img.caption}</p>
    </figcaption>
  </motion.figure>
);

export const Gallery = () => (
  <section id="results" data-testid="results-gallery-section" className="bg-slate-50 py-24 lg:py-32">
    <div className="mx-auto max-w-7xl px-6 lg:px-12">
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.75, ease }}
        className="mb-14 flex flex-col gap-8 lg:mb-20 lg:flex-row lg:items-end lg:justify-between"
      >
        <div className="max-w-2xl">
          <p className="mb-4 text-xs font-bold uppercase tracking-[0.25em] text-teal-700">Results Gallery</p>
          <h2 className="font-serif text-4xl font-medium tracking-tight text-slate-900 sm:text-5xl">
            The proof is in <span className="italic text-teal-700">the shine.</span>
          </h2>
          <p className="mt-5 text-sm leading-relaxed text-slate-500 sm:text-base">
            Every Hyek visit ends the same way — rooms that gleam, fabrics revived, and that
            just-cleaned freshness you can feel the moment you walk in.
          </p>
        </div>
        <button
          data-testid="gallery-quote-button"
          onClick={() => scrollToSection("#contact")}
          className="group flex w-fit items-center gap-2 rounded-full bg-teal-700 px-7 py-3.5 text-sm font-bold text-white shadow-sm transition-transform duration-300 hover:scale-105 hover:bg-teal-600"
        >
          Get results like these
          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </button>
      </motion.div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4 lg:gap-6">
        {GALLERY.map((img, i) => (
          <Tile key={img.caption} img={img} index={i} />
        ))}
      </div>
    </div>
  </section>
);
